import {useEffect, useState} from 'react';

import DeviceMgr from 'coreClasses/device/DeviceMgr';
import {useHistory, useRequestClass} from 'coreHooks';
import {getHostnameByCountryCode} from 'coreUtils/url';
import {getConfig} from 'coreUtils/utils';
import {getDefaultSite} from 'coreUtils/siteUtils';
import {MULTISITE_AVAILABLE} from 'brandConstants';
import {useGeneralProvider} from 'brand/app/components/_app-config/GeneralProvider';
import {useLocalesMgr} from 'coreHooks';
import {requestIdleCallback, cancelIdleCallback} from 'coreUtils/utils';

const useUpdateLocale = (props) => {
	const {managerRepository} = useGeneralProvider();
	const localesMgr = useLocalesMgr();
	const currentLocale = localesMgr.getCurrentLocale();
	const history = useHistory();
	const requestClass = useRequestClass();
	const sitesIdArray = props.sitesId?.split(',') || [];
	const [cookieProcessed, setCookieProcessed] = useState(false);
	const {instanceKey} = useRequestClass();

	const isSameSiteId = (sitesIdArray) => {
		const currentSiteId = managerRepository.getLocalesMgr()?.getCurrentCountry()?.getSiteId();

		return currentSiteId === getRealSiteId(sitesIdArray);
	};

	const getRealSiteId = (sitesIdArray) => {
		const universalSitesId = [getDefaultSite()?.id, 'all'];
		const specificSitesId = sitesIdArray.filter((siteId) => (
			!universalSitesId.includes(siteId) && MULTISITE_AVAILABLE?.includes(siteId)
		));

		return specificSitesId?.[0] || universalSitesId[0];
	};

	useEffect(() => {
		const backForthListener = history.listen((location, action) => {
			if (!location.pathname.includes('COCart-Show') && action === 'POP') {
				const appConfig = getConfig();
				const localeFromURL = localesMgr.parseLocaleFromURL(appConfig.app.instanceHostname, location.pathname);
				const localeFromCookies = localesMgr.getLocaleFromCookies();

				if (
					localeFromURL.isValid() &&
					localeFromCookies.isValid() &&
					localeFromURL.getCode() !== localeFromCookies.getCode()
				) {
					localesMgr.updateLocale(localeFromURL.getCountryCode(), localeFromURL.getLanguageCode());
				}
			}
		});

		return () => {
			backForthListener();
		};
	}, []);

	useEffect(() => {
		let callbackId;

		const cookieLocale = localesMgr.getLocaleFromCookies();

		const doneProcessing = () => {
			localesMgr.updateCookiesWithCurrentValues();
			setCookieProcessed(true);
		};

		const currencyDiscrepancy =
				currentLocale.getCode() === cookieLocale.getCode() &&
				currentLocale.getCurrencyCode() !== localStorage.getItem('scapi:currency');

		if (requestClass.isLivePreview && sitesIdArray.length && !isSameSiteId(sitesIdArray)) {
			const countries = managerRepository.getCountriesMgr().getCountries();
			const newSiteId = getRealSiteId(sitesIdArray);
			const newCountryCode = countries.find((c) => c.getSiteId() === newSiteId)?.getCountryCode() || 'GB';
			const newCountryModel = countries.find((c) => c.getCountryCode() === newCountryCode);
			const newLaguageCode = newCountryModel?.getDefaultLanguage() || 'en';

			callbackId = requestIdleCallback(() => {
				localesMgr.updateLocale(newCountryCode, newLaguageCode).then(doneProcessing);
			});
		} else if (
			cookieLocale.isValid() &&
			(!currentLocale.isValid() || currentLocale.getCode() !== cookieLocale.getCode() || currencyDiscrepancy)
		) {
			if (currencyDiscrepancy) {
				managerRepository.getLocalesMgr().setCountry(currentLocale.getCountryCode());
			}

			const currentSiteId = managerRepository.getLocalesMgr()?.getCurrentCountry()?.getSiteId();
			const redirectHostname = getHostnameByCountryCode(cookieLocale.getCountryCode(), currentSiteId, instanceKey);
			const appConfig = getConfig();

			if (window?.navigator && !DeviceMgr.isBot(window.navigator.userAgent) &&
				(!redirectHostname || appConfig.app.instanceHostname === redirectHostname)) {

				callbackId = requestIdleCallback(() => {
					localesMgr.updateLocale(cookieLocale.getCountryCode(), cookieLocale.getLanguageCode())
						.then(doneProcessing);
				});
			} else {
				doneProcessing();
			}
		} else {
			doneProcessing();
		}

		return () => cancelIdleCallback(callbackId);
	}, []);

	return {cookieProcessed, setCookieProcessed};
};

export default useUpdateLocale;
